import React from 'react';
import dayjs from 'dayjs';
import { getVMRecommendedSizing } from '../../lib/helpers';
import { RivermeadowTag, Typography, Tooltip, Badge } from '../../components';
import { WindowsLogo, LinuxLogo } from '../../components/icons';
import { PreflightStatusIcon } from './components/PreflightStatusIcon';

// Group sources by move groups
export const groupSourcesByMoveGroup = (sources) => {
    if (!sources?.content) return [];

    const groupedData = {};

    sources.content.forEach((source) => {
        const moveGroupId = source.move_group?.id || 'no-group';
        const moveGroupName = source.move_group?.name || 'No Move Group';

        if (!groupedData[moveGroupId]) {
            groupedData[moveGroupId] = {
                key: moveGroupId,
                moveGroupName,
                moveGroup: source.move_group,
                sources: [],
                isGroup: true,
            };
        }

        groupedData[moveGroupId].sources.push({
            ...source,
            key: source.id,
            isGroup: false,
        });
    });

    // Convert to array and flatten structure for table
    const result = [];
    Object.values(groupedData).forEach((group) => {
        // Add group header
        result.push({
            key: `group-${group.key}`,
            isGroup: true,
            moveGroupName: group.moveGroupName,
            moveGroup: group.moveGroup,
            sources: group.sources,
            children: group.sources,
        });
    });

    return result;
};

// Get migration method based on collection type
export const getMigrationMethod = (source = {}) => {
    if (source.collection_type === 'vm') {
        return 'VM-Based';
    }
    return 'OS-Based';
};

// Get migration readiness status with icons
export const getMigrationReadiness = (source = {}) => {
    return <PreflightStatusIcon source={source} />;
};

// Format tags
export const formatTags = (tags = []) => {
    if (!tags || tags.length === 0) return '-';

    return tags.map((tag) => <RivermeadowTag key={tag}>{tag}</RivermeadowTag>);
};

// Format tags
export const cloudSizing = (item = {}) => {
    if (item.collection_type === 'vm' && item?.attributes?.vm_perf_metrics?.timestamps?.length !== 0) {
        return getVMRecommendedSizing(item);
    } else {
        if (item.cloud_sizing && item.cloud_sizing.indexOf(':') > 0) {
            return item.cloud_sizing.split(':')[1];
        }
        return item.cloud_sizing;
    }
};

// Parse stored instructions JSON back into display string
export const parseSourceInstructions = (instructions) => {
    if (!instructions || instructions === '{}') {
        return '';
    }

    const entries = Object.entries(JSON.parse(instructions));
    const hasNumericKeys = entries.every(([key]) => !isNaN(parseInt(key)));

    if (hasNumericKeys) {
        const sortedEntries = entries.sort(([a], [b]) => parseInt(a) - parseInt(b));
        const reconstructed = sortedEntries.map(([, value]) => value).join('');
        return reconstructed;
    } else {
        return entries.map(([key, value]) => `${key}=${Array.isArray(value) ? value.join(',') : value}`).join(';');
    }
};

// Format created date
export const formatSourceDate = (dateString) => {
    if (!dateString) return '-';
    return dayjs(dateString).format('MM/DD/YYYY HH:mm:ss');
};

// Get OS description
export const getOSDescription = (source = {}) => {
    const { os_type, attributes } = source;
    let osIcon = null;
    if (os_type === 'windows') {
        osIcon = <WindowsLogo cloudIconMedium />;
    }
    if (os_type === 'linux') {
        osIcon = <LinuxLogo cloudIconMedium />;
    }
    if (!osIcon) return null;
    return <Tooltip title={attributes?.os?.desc}>{osIcon}</Tooltip>;
};

// Get power state with proper icon
export const getPowerState = (source = {}) => {
    const { power_state } = source;
    if (!power_state) return '-';

    const stateMap = {
        poweredOn: <Badge status="success" text="Running" />,
        poweredOff: <Badge status="error" text="Off" />,
        suspended: <Badge status="warning" text="Unknown" />,
    };

    return (
        stateMap[power_state] || <Badge style={{ textTransform: 'capitalize' }} status="default" text={power_state} />
    );
};
